type ReplayMode = "journey" | "checkout";
type SampleBasis = "user_month" | "checkout_trigger";
type ReplayStage =
  | "app_landed"
  | "authenticated"
  | "contact_ready"
  | "message_created"
  | "checkout_viewed"
  | "send_attempted"
  | "message_sent"
  | "paid_signal";
type ReplayErrorCategory = "payment" | "validation" | "send" | "exception" | "unknown";

type UserBooleanField =
  | "hasPurchasedCredits"
  | "hasPaidInvoice"
  | "hasSubscription"
  | "hasSentLetter";

type CheckoutReplayContext = {
  letterId?: number | string;
  messageType?: string;
  shippingMethod?: string;
  paymentRequired?: boolean | null;
  creditOption?: number | string | null;
  priceCents?: number | null;
  offerRedemptionId?: string | null;
  dpHasOffers?: boolean | null;
};

type PosthogReplayUserFields = {
  hasPurchasedCredits?: boolean | null;
  has_purchased_credits?: boolean | null;
  hasPaidInvoice?: boolean | null;
  has_paid_invoice?: boolean | null;
  hasSubscription?: boolean | null;
  has_subscription?: boolean | null;
  hasSentLetter?: boolean | null;
  has_sent_letter?: boolean | null;
  createdAt?: string | null;
  created_at?: string | null;
  deviceInfo?: { metrics?: boolean };
  device_info?: { metrics?: boolean };
};

type PosthogReplayUser = PosthogReplayUserFields & {
  id?: number | string | null;
  raw?: PosthogReplayUserFields & { [key: string]: unknown };
};

type ReplayStageProperties = CheckoutReplayContext & {
  sourceEvent?: string;
  source_event?: string;
  penmateId?: number | string | null;
  penmate_id?: number | string | null;
  paymentRequired?: boolean | null;
  payment_required?: boolean | null;
};

type ReplayErrorProperties = {
  sourceEvent?: string;
  source_event?: string;
  canonicalStage?: "checkout_viewed" | "send_attempted";
  canonical_stage?: "checkout_viewed" | "send_attempted";
  errorCategory?: ReplayErrorCategory;
  error_category?: ReplayErrorCategory;
  messageType?: string | null;
  message_type?: string | null;
  shippingMethod?: string | null;
  shipping_method?: string | null;
  paymentRequired?: boolean | null;
  payment_required?: boolean | null;
};

let initialized = false;
let journeyReplayActive = false;
let checkoutReplayActive = false;
let replayStartedEventCaptured = false;
let lastUserId: string | null = null;
let activeJourneyUserId: string | null = null;
let activeCheckoutTriggerKey: string | null = null;
let stopTimer: number | null = null;
let maxDurationTimer: number | null = null;
let maxDurationDeadlineMs: number | null = null;

const DEFAULT_JOURNEY_SAMPLE_RATE = 0.2;
const DEFAULT_CHECKOUT_SAMPLE_RATE = 0.5;
const DEFAULT_NEW_USER_CHECKOUT_SAMPLE_RATE = 1;
const DEFAULT_PRIOR_PAYER_CHECKOUT_SAMPLE_RATE = 0.1;
const DEFAULT_MAX_JOURNEY_SESSIONS_PER_USER = 6;
const DEFAULT_JOURNEY_WINDOW_DAYS = 14;
const DEFAULT_JOURNEY_MAX_DURATION_MS = 30 * 60 * 1000;
const DEFAULT_CHECKOUT_MAX_DURATION_MS = 15 * 60 * 1000;

const currentMonthKey = (): string => {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const checkoutTriggerExhaustedKey = (triggerKey: string): string =>
  `pm_posthog_checkout_trigger_exhausted|${triggerKey}|${currentMonthKey()}`;

const PAYMENT_RETURN_IDENTIFIER_PARAMS = [
  "session_id",
  "setup_intent",
  "setup_intent_client_secret",
  "payment_intent",
  "payment_intent_client_secret",
] as const;

const PAYMENT_URL_PARAMS = [
  ...PAYMENT_RETURN_IDENTIFIER_PARAMS,
  "redirect_status",
] as const;

const markCheckoutTriggerExhausted = (triggerKey: string): void => {
  try {
    window.sessionStorage.setItem(checkoutTriggerExhaustedKey(triggerKey), "1");
  } catch {
  }
};

const isCheckoutTriggerExhausted = (triggerKey: string): boolean => {
  if (shouldForceSample()) return false;
  try {
    return window.sessionStorage.getItem(checkoutTriggerExhaustedKey(triggerKey)) === "1";
  } catch {
    return false;
  }
};

const clearStopTimer = (): void => {
  if (stopTimer != null) {
    window.clearTimeout(stopTimer);
    stopTimer = null;
  }
};

const clearMaxDurationTimer = (): void => {
  if (maxDurationTimer != null) {
    window.clearTimeout(maxDurationTimer);
    maxDurationTimer = null;
  }
  maxDurationDeadlineMs = null;
};

const setMaxDurationTimer = (durationMs: number, options: { preferEarlierDeadline?: boolean } = {}): void => {
  if (durationMs <= 0) return;
  const deadlineMs = Date.now() + durationMs;
  if (
    options.preferEarlierDeadline &&
    maxDurationDeadlineMs != null &&
    maxDurationDeadlineMs <= deadlineMs
  ) {
    return;
  }
  clearMaxDurationTimer();
  maxDurationDeadlineMs = deadlineMs;
  maxDurationTimer = window.setTimeout(() => {
    const checkoutTriggerToExhaust = checkoutReplayActive ? activeCheckoutTriggerKey : null;
    if (journeyReplayActive && activeJourneyUserId) {
      markJourneySessionStopped(activeJourneyUserId);
    }
    if (checkoutTriggerToExhaust) {
      markCheckoutTriggerExhausted(checkoutTriggerToExhaust);
    }
    stopAllReplays();
  }, Math.max(0, deadlineMs - Date.now()));
};

const shouldForceSample = (): boolean => {
  if (typeof window === "undefined") return false;
  const w = window as any;
  return w && w.__pm_force_posthog_replay_sample === true;
};

const getPosthog = (): any => {
  if (typeof window === "undefined") return null;
  return (window as any).posthog;
};

const getPenmateEnv = (): any => {
  if (typeof window === "undefined") return null;
  const w = window as any;
  return w && w.Penmate && w.Penmate.env ? w.Penmate.env : null;
};

const redactPaymentUrlParams = (rawUrl: unknown): string | null => {
  if (typeof rawUrl !== "string" || !rawUrl.trim()) return null;
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://penmateapp.com";
    const url = new URL(rawUrl, base);
    let changed = false;
    for (const param of PAYMENT_URL_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        changed = true;
      }
    }
    return changed ? url.toString() : rawUrl;
  } catch {
    return rawUrl;
  }
};

const sanitizedCurrentUrl = (): string | null => {
  if (typeof window === "undefined") return null;
  return redactPaymentUrlParams(window.location.href);
};

const hasPaymentReturnIdentifiersInUrl = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    return PAYMENT_RETURN_IDENTIFIER_PARAMS.some((param) => url.searchParams.has(param));
  } catch {
    return false;
  }
};

const sanitizeReplayProperties = (properties: Record<string, unknown>): Record<string, unknown> => {
  const next: Record<string, unknown> = { ...properties };
  const currentUrl = sanitizedCurrentUrl();
  if (currentUrl) {
    next.$current_url = currentUrl;
  }

  for (const key of ["$current_url", "current_url", "url", "return_url"]) {
    const redacted = redactPaymentUrlParams(next[key]);
    if (redacted) {
      next[key] = redacted;
    }
  }

  return next;
};

const parseBool = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return fallback;
};

const parseNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const clampRate = (value: number): number => Math.max(0, Math.min(1, value));

const replayEnabled = (): boolean => {
  const env = getPenmateEnv();
  return parseBool(env?.posthogReplayEnabled, false);
};

const journeySampleRate = (): number => {
  const env = getPenmateEnv();
  return clampRate(parseNumber(env?.posthogReplayJourneyUserSampleRate, DEFAULT_JOURNEY_SAMPLE_RATE));
};

const checkoutSampleRate = (): number => {
  const env = getPenmateEnv();
  return clampRate(
    parseNumber(
      env?.posthogReplayCheckoutSampleRate ?? env?.posthogReplaySampleRate,
      DEFAULT_CHECKOUT_SAMPLE_RATE,
    ),
  );
};

const newUserCheckoutSampleRate = (): number => {
  const env = getPenmateEnv();
  return clampRate(
    parseNumber(
      env?.posthogReplayNewUserCheckoutSampleRate ?? env?.posthogReplaySampleRateNewUserPaid,
      DEFAULT_NEW_USER_CHECKOUT_SAMPLE_RATE,
    ),
  );
};

// Env can lower or zero this rate (rollback), but never raise it above the
// D-016 cap — raising the most sensitive cohort's sampling requires a
// deliberate code change.
const priorPayerCheckoutSampleRate = (): number => {
  const env = getPenmateEnv();
  const rate = clampRate(
    parseNumber(env?.posthogReplayPriorPayerCheckoutSampleRate, DEFAULT_PRIOR_PAYER_CHECKOUT_SAMPLE_RATE),
  );
  return Math.min(rate, DEFAULT_PRIOR_PAYER_CHECKOUT_SAMPLE_RATE);
};

const maxJourneySessionsPerUser = (): number => {
  const env = getPenmateEnv();
  return Math.max(
    0,
    Math.round(parseNumber(env?.posthogReplayMaxJourneySessionsPerUser, DEFAULT_MAX_JOURNEY_SESSIONS_PER_USER)),
  );
};

const journeyWindowDays = (): number => {
  const env = getPenmateEnv();
  return Math.max(0, parseNumber(env?.posthogReplayJourneyWindowDays, DEFAULT_JOURNEY_WINDOW_DAYS));
};

const journeyMaxDurationMs = (): number => {
  const env = getPenmateEnv();
  return Math.max(0, Math.round(parseNumber(env?.posthogReplayJourneyMaxDurationMs, DEFAULT_JOURNEY_MAX_DURATION_MS)));
};

const checkoutMaxDurationMs = (): number => {
  const env = getPenmateEnv();
  return Math.max(0, Math.round(parseNumber(env?.posthogReplayCheckoutMaxDurationMs, DEFAULT_CHECKOUT_MAX_DURATION_MS)));
};

const initIfNeeded = (): void => {
  try {
    if (initialized) return;
    if (typeof window === "undefined") return;
    if (!replayEnabled()) return;

    const env = getPenmateEnv();
    if (!env) return;

    const posthog = getPosthog();
    if (!posthog) return;

    const apiKey = env.posthogProjectId;
    if (!apiKey || typeof apiKey !== "string" || !apiKey.trim().length) return;

    const alreadyInitialized =
      !!(posthog && posthog.config && (posthog.config.token || posthog.config.api_token));

    if (!alreadyInitialized && posthog.init) {
      posthog.init(apiKey, {
        api_host: "https://us.i.posthog.com",
        autocapture: false,
        capture_pageview: false,
        disable_session_recording: true,
        session_recording: {
          maskAllInputs: true,
          blockClass: "ph-no-capture",
          maskTextClass: "ph-mask",
        },
        sanitize_properties: sanitizeReplayProperties,
      });
    }

    initialized = true;
  } catch {
    initialized = false;
  }
};

const getUserId = (user: PosthogReplayUser | null | undefined): string | null => {
  const raw = user?.id;
  if (raw == null) return null;
  const value = String(raw).trim();
  return value ? value : null;
};

const boolValue = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes"].includes(normalized)) return true;
    if (["false", "0", "no"].includes(normalized)) return false;
  }
  return null;
};

const userBooleanField = (
  user: PosthogReplayUser | null | undefined,
  camelKey: UserBooleanField,
  snakeKey: string,
): boolean | null => {
  return (
    boolValue((user as any)?.[camelKey]) ??
    boolValue((user as any)?.[snakeKey]) ??
    boolValue((user as any)?.raw?.[camelKey]) ??
    boolValue((user as any)?.raw?.[snakeKey])
  );
};

const hasPurchasedCredits = (user: PosthogReplayUser | null | undefined): boolean => {
  return userBooleanField(user, "hasPurchasedCredits", "has_purchased_credits") === true;
};

const hasPaidInvoice = (user: PosthogReplayUser | null | undefined): boolean => {
  return userBooleanField(user, "hasPaidInvoice", "has_paid_invoice") === true;
};

const hasSubscription = (user: PosthogReplayUser | null | undefined): boolean => {
  return userBooleanField(user, "hasSubscription", "has_subscription") === true;
};

const hasPaidOrPurchased = (user: PosthogReplayUser | null | undefined): boolean =>
  hasPurchasedCredits(user) || hasPaidInvoice(user) || hasSubscription(user);

const hasSentLetter = (user: PosthogReplayUser | null | undefined): boolean | null => {
  return userBooleanField(user, "hasSentLetter", "has_sent_letter");
};

const isDemoUser = (user: PosthogReplayUser | null | undefined): boolean => {
  if (!user || typeof user !== "object") return false;
  const metrics =
    (user as any)?.deviceInfo?.metrics ??
    (user as any)?.device_info?.metrics ??
    (user as any)?.raw?.deviceInfo?.metrics ??
    (user as any)?.raw?.device_info?.metrics;
  return metrics === false;
};

const createdAtMs = (user: PosthogReplayUser, userId: string): number | null => {
  const raw =
    user.createdAt ??
    user.created_at ??
    user.raw?.createdAt ??
    user.raw?.created_at ??
    null;
  if (typeof raw === "string" && raw.trim()) {
    const parsed = Date.parse(raw);
    if (Number.isFinite(parsed)) return parsed;
  }

  const key = `pm_posthog_journey_first_seen|${userId}`;
  try {
    const stored = window.localStorage.getItem(key);
    if (stored) {
      const parsed = Number.parseInt(stored, 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    const now = Date.now();
    window.localStorage.setItem(key, String(now));
    return now;
  } catch {
    return null;
  }
};

const withinJourneyWindow = (user: PosthogReplayUser, userId: string): boolean => {
  const days = journeyWindowDays();
  if (days <= 0 || shouldForceSample()) return true;
  const createdAt = createdAtMs(user, userId);
  if (createdAt == null) return false;
  const ageMs = Date.now() - createdAt;
  return ageMs <= days * 24 * 60 * 60 * 1000;
};

const journeySessionCountKey = (userId: string): string =>
  `pm_posthog_journey_sessions|${userId}|${currentMonthKey()}`;

const journeySessionMarkerKey = (userId: string): string =>
  `pm_posthog_journey_session_started|${userId}|${currentMonthKey()}`;

const journeySessionStoppedKey = (userId: string): string =>
  `pm_posthog_journey_session_stopped|${userId}|${currentMonthKey()}`;

const readInt = (key: string): number | null => {
  try {
    const value = window.localStorage.getItem(key);
    if (!value) return 0;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  } catch {
    return null;
  }
};

const hasJourneySessionCapacity = (userId: string): boolean => {
  if (shouldForceSample()) return true;
  const maxSessions = maxJourneySessionsPerUser();
  if (maxSessions <= 0) return false;
  try {
    if (window.sessionStorage.getItem(journeySessionStoppedKey(userId)) === "1") return false;
    if (window.sessionStorage.getItem(journeySessionMarkerKey(userId)) === "1") return true;
  } catch {
    return false;
  }
  const count = readInt(journeySessionCountKey(userId));
  if (count == null) return false;
  return count < maxSessions;
};

const markJourneySessionStopped = (userId: string): void => {
  try {
    window.sessionStorage.setItem(journeySessionStoppedKey(userId), "1");
  } catch {
  }
};

const markJourneySessionStarted = (userId: string): void => {
  try {
    const markerKey = journeySessionMarkerKey(userId);
    if (window.sessionStorage.getItem(markerKey) === "1") return;
    window.sessionStorage.setItem(markerKey, "1");

    const countKey = journeySessionCountKey(userId);
    window.localStorage.setItem(countKey, String((readInt(countKey) ?? 0) + 1));
  } catch {
    // If browser storage is unavailable, keep recording for the active sampled session.
  }
};

const shouldSampleUserThisMonth = (prefix: string, userId: string, sampleRate: number): boolean => {
  if (shouldForceSample()) return true;
  if (sampleRate <= 0) return false;
  if (sampleRate >= 1) return true;

  const key = `${prefix}|${userId}|${currentMonthKey()}`;
  return hashToUnitInterval(key) < sampleRate;
};

const checkoutTriggerSampleKey = (userId: string, ctx: CheckoutReplayContext): string => {
  const triggerId = normalizeNullableString(ctx.letterId) ?? "unknown";
  return `${userId}|${triggerId}`;
};

const isAnyReplayActive = (): boolean => journeyReplayActive || checkoutReplayActive;

// Inverse of stopCheckoutReplay's dual-mode handling: keep the live
// recording but drop its journey identity, tightening to the checkout cap.
const demoteJourneyToCheckout = (): void => {
  journeyReplayActive = false;
  activeJourneyUserId = null;
  setMaxDurationTimer(checkoutMaxDurationMs(), { preferEarlierDeadline: true });
};

const handleUserBoundary = (posthog: any, userId: string): void => {
  if (lastUserId && lastUserId !== userId) {
    stopAllReplays();
    try {
      if (posthog.reset) posthog.reset(true);
    } catch {
    }
  }
  lastUserId = userId;
};

const startRecording = (
  user: PosthogReplayUser,
  mode: ReplayMode,
  sampleRate: number,
  sampleBasis: SampleBasis,
  triggerStage: string,
  maxDurationMs: number,
  extraProperties: Record<string, unknown> = {},
): boolean => {
  const posthog = getPosthog();
  const userId = getUserId(user);
  if (!posthog || !posthog.startSessionRecording || !userId) return false;
  if (maxDurationMs <= 0) return false;

  handleUserBoundary(posthog, userId);

  if (isAnyReplayActive()) {
    if (mode === "journey" && !journeyReplayActive) {
      journeyReplayActive = true;
      activeJourneyUserId = userId;
      setMaxDurationTimer(maxDurationMs);
    }
    return true;
  }

  try {
    posthog.identify(userId);
    posthog.startSessionRecording({ sampling: true, event_trigger: true });
  } catch {
    return false;
  }

  if (mode === "journey") {
    journeyReplayActive = true;
    activeJourneyUserId = userId;
  } else {
    checkoutReplayActive = true;
  }

  setMaxDurationTimer(maxDurationMs);
  captureReplayStarted(user, {
    replay_mode: mode,
    trigger_stage: triggerStage,
    sample_rate: sampleRate,
    sample_basis: sampleBasis,
    ...extraProperties,
  });
  return true;
};

const captureReplayStarted = (user: PosthogReplayUser, properties: Record<string, unknown>): void => {
  if (replayStartedEventCaptured) return;
  replayStartedEventCaptured = true;

  const userId = getUserId(user);
  captureReplayEvent("pm_replay_started", {
    replay_mode: properties.replay_mode,
    trigger_stage: properties.trigger_stage,
    sample_rate: properties.sample_rate,
    sample_basis: properties.sample_basis,
    platform: "ionic_webview",
    app_user_id: userId,
    has_sent_letter: hasSentLetter(user),
    has_purchased_credits: hasPurchasedCredits(user),
    sampled_stratum: properties.sampled_stratum ?? null,
    journey_window_days: properties.journey_window_days ?? null,
    max_sessions_per_user: properties.max_sessions_per_user ?? null,
  });
};

const captureReplayEvent = (event: string, properties: Record<string, unknown>): void => {
  if (!isAnyReplayActive()) return;
  try {
    const posthog = getPosthog();
    if (!posthog || !posthog.capture) return;
    posthog.capture(event, sanitizeReplayProperties({ ...properties, $geoip_disable: true }));
  } catch {
  }
};

const normalizeNullableString = (value: unknown): string | null => {
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
};

const normalizeNullableNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const replayStageProperties = (props: ReplayStageProperties = {}): Record<string, unknown> => ({
  source_event: normalizeNullableString(props.sourceEvent ?? props.source_event),
  letter_id: normalizeNullableString(props.letterId),
  penmate_id: normalizeNullableString(props.penmateId ?? props.penmate_id),
  message_type: normalizeNullableString(props.messageType),
  shipping_method: normalizeNullableString(props.shippingMethod),
  payment_required: props.paymentRequired ?? props.payment_required ?? null,
  credit_option: normalizeNullableString(props.creditOption),
  price_cents: normalizeNullableNumber(props.priceCents),
  offer_redemption_id: normalizeNullableString(props.offerRedemptionId),
  dp_has_offers: props.dpHasOffers ?? null,
});

export const maybeStartJourneyReplay = (user: PosthogReplayUser | null | undefined): void => {
  try {
    initIfNeeded();
    if (!initialized || !replayEnabled()) return;
    if (hasPaymentReturnIdentifiersInUrl()) return;

    const posthog = getPosthog();
    const userId = getUserId(user);
    if (!posthog || !user || !userId) return;

    handleUserBoundary(posthog, userId);

    if (isDemoUser(user)) {
      stopAllReplays();
      return;
    }

    if (hasPaidOrPurchased(user)) {
      // D-016: a prior payer may hold a checkout-trigger recording; the
      // journey bootstrap must not tear it down (it re-runs on every auth
      // refetch). Journey recordings still never include prior payers:
      // journey-only stops, dual mode demotes to checkout-only. Rate 0
      // restores the original full stop.
      if (priorPayerCheckoutSampleRate() <= 0) {
        stopAllReplays();
        return;
      }
      if (journeyReplayActive) {
        if (checkoutReplayActive) {
          demoteJourneyToCheckout();
        } else {
          stopAllReplays();
        }
      }
      return;
    }

    const sampleRate = journeySampleRate();
    if (!shouldSampleUserThisMonth("pm_journey_replay", userId, sampleRate)) return;
    if (!withinJourneyWindow(user, userId)) return;
    if (!hasJourneySessionCapacity(userId)) return;

    const started = startRecording(user, "journey", sampleRate, "user_month", "authenticated", journeyMaxDurationMs(), {
      journey_window_days: journeyWindowDays(),
      max_sessions_per_user: maxJourneySessionsPerUser(),
    });
    if (!started) return;

    markJourneySessionStarted(userId);
    trackReplayStage("app_landed", { sourceEvent: "journey_replay_started" });
    trackReplayStage("authenticated", { sourceEvent: "authenticated" });
  } catch {
  }
};

export const maybeStartCheckoutReplay = (
  user: PosthogReplayUser | null | undefined,
  ctx: CheckoutReplayContext = {},
): void => {
  try {
    initIfNeeded();
    if (!initialized || !replayEnabled()) return;
    if (hasPaymentReturnIdentifiersInUrl()) return;

    const posthog = getPosthog();
    const userId = getUserId(user);
    if (!posthog || !posthog.startSessionRecording || !user || !userId) return;

    handleUserBoundary(posthog, userId);

    if (isDemoUser(user)) {
      stopAllReplays();
      return;
    }

    // D-016: prior payers are eligible for the checkout-trigger sampler
    // only, at a small capped rate; rate 0 restores the original exclusion
    // exactly. Journey replay never records prior payers, so an active
    // journey recording stops here. An active checkout recording follows
    // the same same-trigger continuation and trigger-change rules as
    // never-paid users below, so checkout refreshes cannot restart the
    // recording or renew its max-duration cap.
    const priorPayer = hasPaidOrPurchased(user);
    if (priorPayer) {
      if (priorPayerCheckoutSampleRate() <= 0) {
        stopAllReplays();
        return;
      }
      if (journeyReplayActive) {
        if (checkoutReplayActive) {
          // Dual mode: the checkout side of the recording is legitimate;
          // drop only the journey identity so the same-trigger continuation
          // below keeps the session instead of restarting it.
          demoteJourneyToCheckout();
        } else {
          stopAllReplays();
        }
      }
    }

    const paymentRequired = ctx && ctx.paymentRequired === true;
    if (!paymentRequired) return;

    const triggerKey = checkoutTriggerSampleKey(userId, ctx);

    if (checkoutReplayActive && !journeyReplayActive && activeCheckoutTriggerKey !== triggerKey) {
      stopAllReplays();
    }

    if (isCheckoutTriggerExhausted(triggerKey)) return;

    if (stopTimer != null) {
      clearStopTimer();
    }

    if (isAnyReplayActive()) {
      checkoutReplayActive = true;
      if (!activeCheckoutTriggerKey) {
        activeCheckoutTriggerKey = triggerKey;
      }
      if (!journeyReplayActive) {
        setMaxDurationTimer(checkoutMaxDurationMs(), { preferEarlierDeadline: true });
      }
      return;
    }

    const isNewUser = hasSentLetter(user) === false;
    const effectiveSampleRate = priorPayer
      ? priorPayerCheckoutSampleRate()
      : isNewUser
        ? newUserCheckoutSampleRate()
        : checkoutSampleRate();
    if (!shouldSampleUserThisMonth("pm_checkout_replay", triggerKey, effectiveSampleRate)) return;

    const sampledStratum = priorPayer ? "prior_payer" : isNewUser ? "first_sender" : "prior_sender";
    const started = startRecording(user, "checkout", effectiveSampleRate, "checkout_trigger", "checkout_viewed", checkoutMaxDurationMs(), {
      ...replayStageProperties(ctx),
      sampled_stratum: sampledStratum,
    });
    if (started) {
      activeCheckoutTriggerKey = triggerKey;
    }
  } catch {
  }
};

export const trackReplayStage = (stage: ReplayStage, props: ReplayStageProperties = {}): void => {
  captureReplayEvent("pm_replay_stage", {
    canonical_stage: stage,
    ...replayStageProperties(props),
  });
};

export const trackReplayError = (props: ReplayErrorProperties = {}): void => {
  captureReplayEvent("pm_replay_error", {
    canonical_stage: props.canonicalStage ?? props.canonical_stage ?? "send_attempted",
    source_event: normalizeNullableString(props.sourceEvent ?? props.source_event),
    error_category: props.errorCategory ?? props.error_category ?? "unknown",
    message_type: normalizeNullableString(props.messageType ?? props.message_type),
    shipping_method: normalizeNullableString(props.shippingMethod ?? props.shipping_method),
    payment_required: props.paymentRequired ?? props.payment_required ?? null,
  });
};

export const stopCheckoutReplay = (): void => {
  clearStopTimer();
  if (!checkoutReplayActive) return;
  if (journeyReplayActive) {
    checkoutReplayActive = false;
    activeCheckoutTriggerKey = null;
    return;
  }
  stopAllReplays();
};

export const stopAllReplays = (): void => {
  clearStopTimer();
  clearMaxDurationTimer();
  try {
    if (initialized) {
      const posthog = getPosthog();
      if (posthog && posthog.stopSessionRecording) {
        posthog.stopSessionRecording();
      }
    }
  } catch {
  }

  journeyReplayActive = false;
  checkoutReplayActive = false;
  replayStartedEventCaptured = false;
  activeJourneyUserId = null;
  activeCheckoutTriggerKey = null;
};

export const scheduleStopCheckoutReplay = (delayMs: number): void => {
  if (!checkoutReplayActive) return;
  if (typeof window === "undefined") return;
  clearStopTimer();
  stopTimer = window.setTimeout(() => {
    stopTimer = null;
    stopCheckoutReplay();
  }, delayMs);
};

export const cancelScheduledStopCheckoutReplay = (): void => {
  if (stopTimer == null) return;
  clearStopTimer();
};

// FNV-1a 32-bit hash -> [0, 1)
const hashToUnitInterval = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash / 0x100000000;
};
